import React, { useState, useRef, useEffect } from 'react';
import './MessageInput.scss';
import imageIcon from '../images/image.png';
import sendIcon from '../images/send.png';
import emojiIcon from '../images/emoji.png';

const MessageInput = ({ onSendMessage, onTyping }) => {
  const [message, setMessage] = useState('');
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const textareaRef = useRef(null);

  // 자주 사용하는 이모지들
  const quickEmojis = [
    '😀', '😂', '🥰', '😢', '😮', '😡', '🤔', '😍', '🥺', '😭', '😎', '🤩',
    '👍', '👎', '👏', '🙌', '🤝', '🙏', '✌️', '👌', '👋', '🤚',
    '❤️', '💙', '💚', '💛', '🧡', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕'
  ];

  // 메시지 전송
  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (message.trim()) {
      onSendMessage(message.trim(), 'text');
      setMessage('');
      setIsEmojiPickerOpen(false);
      textareaRef.current?.focus();
    }
  };

  // 텍스트 영역 키 다운 핸들러
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift + Enter: 줄바꿈
        return;
      } else {
        // Enter: 메시지 전송
        e.preventDefault();
        handleSendMessage(e);
      }
    }
  };

  // 텍스트 변경
  const handleTextChange = (e) => {
    setMessage(e.target.value);
    
    // 타이핑 중임을 알리는 함수가 있다면 호출
    if (onTyping && typeof onTyping === 'function') {
      onTyping();
    }
  };

  // iOS 키보드 Done 후 화면이 안 내려오는 문제 해결
  const handleBlur = () => {
    // visualViewport API: 키보드가 완전히 내려간 시점을 정확히 포착
    if (window.visualViewport) {
      const onResize = () => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      };
      window.visualViewport.addEventListener('resize', onResize, { once: true });
    } else {
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }, 150);
    }
  };

  // 이모지 선택
  const handleEmojiSelect = (emoji) => {
    setMessage(prev => prev + emoji);
    setIsEmojiPickerOpen(false);
    textareaRef.current?.focus();
  };

  // 클립보드 붙여넣기 처리
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // 이미지 파일인 경우
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault(); // 기본 붙여넣기 동작 방지
        
        const file = item.getAsFile();
        if (file) {
          await uploadImageFile(file);
        }
        return;
      }
    }
  };

  // 이미지 파일 업로드 (클립보드와 파일 선택에서 공통 사용)
  const uploadImageFile = async (file) => {
    setIsFileUploading(true);
    
    try {
      // Firebase Storage에 이미지 업로드
      const { ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
      const { storage } = await import('../firebaseConfig');
      
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substr(2, 9);
      const fileName = `images/${timestamp}_${randomId}_${file.name || 'clipboard_image.png'}`;
      const imageRef = storageRef(storage, fileName);
      
      console.log('이미지 업로드 시작:', fileName);
      
      // 이미지 업로드
      const uploadTask = uploadBytesResumable(imageRef, file);
      
      const downloadURL = await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`이미지 업로드 진행률: ${progress}%`);
          },
          (error) => {
            console.error('이미지 업로드 실패:', error);
            reject(error);
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          }
        );
      });

      // 이미지 데이터 구성
      const imageData = [{
        url: downloadURL,
        name: file.name || 'clipboard_image.png',
        size: file.size,
        type: 'image'
      }];

      // 이미지 메시지로 바로 전송
      onSendMessage('', 'image', imageData);
      console.log('이미지 업로드 및 전송 완료:', imageData);
      
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      alert('이미지 업로드에 실패했습니다: ' + error.message);
    } finally {
      setIsFileUploading(false);
    }
  };

  // 이미지 선택
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  // 사진 촬영
  const handleCameraSelect = () => {
    cameraInputRef.current?.click();
  };

  // 이미지 업로드 처리
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type.startsWith('image/')) {
      await uploadImageFile(file);
    }
    e.target.value = '';
  };

  return (
    <div className="message-input-container">
      {/* 이모지 피커 */}
      {isEmojiPickerOpen && (
        <div className="emoji-picker">
          <div className="emoji-grid">
            {quickEmojis.map((emoji, index) => (
              <button
                key={index}
                className="emoji-item"
                onClick={() => handleEmojiSelect(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
          <button 
            className="close-emoji-picker"
            onClick={() => setIsEmojiPickerOpen(false)}
          >
            ✕
          </button>
        </div>
      )}

      <form onSubmit={handleSendMessage} className="message-input-form">
        <div className="input-actions-left">
          <button
            type="button"
            className="action-btn file-btn"
            onClick={handleFileSelect}
            disabled={isFileUploading}
            title="이미지 업로드"
          >
            {isFileUploading ? '⏳' : <img src={imageIcon} alt="image" className="btn-icon" />}
          </button>

          <button
            type="button"
            className="action-btn camera-btn"
            onClick={handleCameraSelect}
            disabled={isFileUploading}
            title="사진 촬영"
            aria-label="사진 촬영"
          >
            <span className="camera-icon" aria-hidden="true" />
          </button>
        </div>

        <div className="input-field-wrapper">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={handleBlur}
            placeholder="메시지 입력..."
            className="message-input"
            rows={1}
            disabled={isFileUploading}
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        </div>

        <button
          type="button"
          className="action-btn emoji-btn"
          onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
          title="이모지"
        >
          <img src={emojiIcon} alt="emoji" className="btn-icon" />
        </button>

        <button
          type="submit"
          className={`send-btn ${message.trim() ? 'active' : ''}`}
          disabled={!message.trim() || isFileUploading}
          title="전송"
        >
          <img src={sendIcon} alt="send" className="btn-icon" />
        </button>
      </form>

      {/* 이미지 전용 파일 입력 */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={cameraInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default MessageInput;
